import { FC, useMemo } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

interface PaginatorProps {
  currentPage: number;
  onPageChange: any;
  totalPages: number;
}

const createPageItems = (currentPage: number, totalPages: number) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      "...",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

const Paginator: FC<PaginatorProps> = ({
  currentPage,
  onPageChange,
  totalPages,
}) => {
  const { t } = useTranslation("table");

  const pageItems = useMemo(
    () => createPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  return (
    <div className="d-flex flex-row flex-wrap justify-content-center align-items-center gap-2">
      <Button
        className="custom-primary-button"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        {`< ${t("previous")}`}
      </Button>

      {pageItems.map((item, index) =>
        item === "..." ? (
          <span key={`ellipsis-${index}`} className="px-2">
            ...
          </span>
        ) : (
          <Button
            key={`page-${item}`}
            className="custom-primary-button"
            variant={item === currentPage ? "primary" : "outline-primary"}
            onClick={() => onPageChange(item)}
            disabled={item === currentPage}
          >
            {item}
          </Button>
        )
      )}

      <Button
        className="custom-primary-button"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        {`${t("next")} >`}
      </Button>
    </div>
  );
};

export default Paginator;
